import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/auth/AuthContext";
import constants from "../../Utils/constants";
import Loader from "../../components/Loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TenantContext from "../../context/tenant/TenantContext";
import useTranslator from "../../hooks/useTranslator";
import { haveValue } from "../../helper/common";
import AuthenticateYourId from "../onboarding/authenticateYourId";
import BasicInfoModal from "../onboarding/BasicInfoModal";
import LandLordContext from "../../context/landlord/LandLordContext";
import TenantAdditionalChecks from "./TenantAdditionalChecks";
import TenantChecksUploadDocument from "./TenantChecksUploadDocument";
import TenantChecksIncomeModel from "./TenantChecksIncomeModel";
import ProcessPassedModal from "../onboarding/ProcessPassedModal";

function TenantChecksOnboarding() {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const authContext = useContext(AuthContext);
  const { user: originalUser } = authContext;
  const { submitProtectedHousingDocuments } = useContext(TenantContext);
  const {
    createTenant,
    createLLGuarantor,
    fetchingCreatedTenant,
    updateLLTenant,
  } = useContext(LandLordContext);
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [guarantorFormData, setGuarantorFormData] = useState({});
  const [pageLoading, setPageLoading] = useState(false);
  const [user, setUser] = useState({});
  const [additionalInfo, setAdditionalInfo] = useState([]);
  const [showPassedModal, setShowPassedModal] = useState(false);

  const queryParameters = new URLSearchParams(window.location.search);
  const tenant_id = queryParameters.get("tenant_id");
  const reCheck = queryParameters.get("reCheck");

  // For Edit user
  const [runDetectKycDocs, setRunDetectKycDocs] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [guarantorPhoneNumber, setGuarantorPhoneNumber] = useState("");
  const [selectedProtectedHousingDocs, setSelectedProtectedHousingDocs] =
    useState([]);
  const [addedFiles, setAddedFiles] = useState([]);

  // Share profile logic ends
  const applyProfileData = (
    _user,
    _setFormData,
    _setPhoneNumber,
    llGuarantor
  ) => {
    _setFormData({
      fname: _user?.fname || "",
      lname: _user?.lname || "",
      identity_number: _user?.dni_nie || _user?.passport_id || "",
      post_code: _user?.post_code || "",
      dob: _user?.dob ? _user.dob.split("T")[0] : "",
      employment_status: _user?.employment_status || "",
      employment_category: _user?.employment_category || "",
      current_annual_income: _user?.current_annual_income ?? "",
      protected_housing: _user?.protected_housing || false,
      email: _user?.email || "",
      gurantor_relation: _user?.gurantor_relation || "",
      phone_number: _user?.phone_number || "",
      currency: _user?.currency || "EUR",
    });
    _setPhoneNumber(_user?.phone_number || "");

    if (!llGuarantor) {
      setSelectedProtectedHousingDocs(
        !_user?.protected_housing
          ? ["no"]
          : _user?.tenant_extra_field_id?.public_housing_docs
      );
      if (haveValue(reCheck) && reCheck) {
        setStep(6);
      } else {
        setStep(_user?.step ?? 1);
      }
      setAdditionalInfo(
        _user?.tenant_extra_field_id?.tenant_checks?.additional_info ?? []
      );
    }
  };

  const loadData = async (needLoading = true, customTenantId) => {
    try {
      if (needLoading) {
        setPageLoading(true);
      }
      let userData;

      if (tenant_id || customTenantId) {
        userData = await fetchingCreatedTenant(tenant_id ?? customTenantId);
      }
      const user = userData?.data ?? {};
      setUser(user);

      // For Tenant user
      applyProfileData(user, setFormData, setPhoneNumber);

      // For guarantor user
      const guarantorData = user?.gurantor_id?.[0] ?? {};
      if (guarantorData) {
        guarantorData.gurantor_relation =
          user?.gurantor_added_id?.[0]?.gurantor_relation;
      }
      applyProfileData(
        haveValue(guarantorData) ? guarantorData : {},
        setGuarantorFormData,
        setGuarantorPhoneNumber,
        true
      );
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [location.pathname]);

  const createUserProfile = async (data, userType) => {
    try {
      // Ensure "is_edit" always has a value
      if (!haveValue(data?.is_edit)) {
        data.is_edit = true;
      }

      // Assign role
      data.role = userType === "llGuarantor" ? "llGuarantor" : "llTenant";

      // If creating guarantor, update tenant's step
      if (userType === "llGuarantor") {
        const _step = getNextStep(step);
        await updateUserProfile({ id: user?._id, step: _step });
      } else {
        data.step = 2;
      }

      let llTTResponse;

      if (userType === "llGuarantor" && user?.gurantor_id?.length === 0) {
        // Create guarantor
        llTTResponse = await createLLGuarantor({ ...data, tenant_id });
      } else if (user?._id) {
        // enant already exists → Update instead of creating
        const user_id =
          userType === "llGuarantor" ? user?.gurantor_id?.[0]?._id : user?._id;
        llTTResponse = await updateLLTenant({ ...data, id: user_id });
      } else {
        //  Create new tenant
        llTTResponse = await createTenant(data);
      }

      //  Only for tenant (not guarantor)
      if (userType !== "llGuarantor") {
        const payload = {
          allDocuments: selectedProtectedHousingDocs ?? [],
          llCreated: true,
          user_id: llTTResponse?.data?.id || user?._id,
        };
        await submitProtectedHousingDocuments(payload);
      }

      // ✅ Update browser URL for new tenant
      if (userType !== "llGuarantor" && !user?._id) {
        const url = new URL(window.location.href);
        url.searchParams.set("tenant_id", llTTResponse?.data?.id);
        window.history.pushState({}, "", url.toString());
      }

      await loadData(false, llTTResponse?.data?.id);
    } catch (err) {
      throw err;
    }
  };

  const updateUserProfile = async (data) => {
    try {
      const response = await updateLLTenant(data);
      loadData(false);
      return response;
    } catch (error) {
      console.log("error", error);
    }
  };

  const requiresSubcategory = (status) => {
    return [
      constants.public_servant,
      constants.employed,
      constants.self_employed,
    ].includes(status);
  };

  const getNextStep = () => {
    const selected = additionalInfo || [];
    const currentStep = step;

    // Step 2 → branching logic
    if (currentStep === 2) {
      if (selected.length === 0) return 5;
      if (selected.includes("TENANT_KYC")) return 3; // KYC now comes first
      if (selected.includes("TENANT_GUARANTOR")) return 4; // Guarantor next
      return 5;
    }

    // Step 3 → after Tenant KYC
    if (currentStep === 3) {
      if (selected.includes("TENANT_GUARANTOR")) return 4; // Go to Guarantor if needed
      return 5;
    }

    // Step 4 → after Guarantor
    if (currentStep === 4) {
      return 5;
    }

    // Step 6 → after Skored
    if (currentStep === 6) return 5;

    // Step 5 → final upload → home
    if (currentStep === 5) {
      if (
        selected.includes("TENANT_SKOR") ||
        selected.includes("GUARANTOR_SKOR")
      ) {
        return 6;
      }

      const user_id = user?._id;
      updateLLTenant({
        profile_completed: true,
        id: user_id,
      });
      setShowPassedModal(true);
      return 5;
    }

    return currentStep + 1;
  };

  const getPreviousStep = () => {
    const selected = additionalInfo || [];
    const currentStep = step;

    if (currentStep === 2) return 1;

    if (currentStep === 3) return 2; // KYC comes after step 2

    if (currentStep === 4) {
      if (selected.includes("TENANT_KYC")) return 3; // Go back to KYC if included
      return 2;
    }

    if (currentStep === 5) {
      // Go back depending on last included step
      if (selected.includes("TENANT_GUARANTOR")) return 4; // Guarantor before upload
      if (selected.includes("TENANT_KYC")) return 3; // Then KYC
      return 2;
    }

    if (currentStep === 6) {
      return 5;
    }

    return currentStep - 1 > 0 ? currentStep - 1 : 1;
  };

  const formHandler = {
    next: () =>
      setStep((prev) => {
        const newStep = getNextStep(prev);
        return newStep;
      }),

    prev: () =>
      setStep((prev) => {
        const newStep = getPreviousStep(prev);
        return newStep;
      }),
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <BasicInfoModal
            user={user}
            formData={formData}
            setFormData={setFormData}
            createUserProfile={createUserProfile}
            formHandler={formHandler}
            requiresSubcategory={requiresSubcategory}
            originalUser={originalUser}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            selectedProtectedHousingDocs={selectedProtectedHousingDocs}
            setSelectedProtectedHousingDocs={setSelectedProtectedHousingDocs}
            usedFor="tenantChecks"
          />
        );
      case 2:
        return (
          <TenantAdditionalChecks
            additionalInfo={additionalInfo}
            setAdditionalInfo={setAdditionalInfo}
            user={user}
            step={step}
            setStep={setStep}
            formHandler={formHandler}
            updateUserProfile={updateUserProfile}
            getNextStep={getNextStep}
          />
        );
      case 3:
        // Guarantor step
        return (
          <AuthenticateYourId
            formHandler={formHandler}
            user={user}
            updateUserProfile={updateUserProfile}
            setRunDetectKycDocs={setRunDetectKycDocs}
            addedFiles={addedFiles}
            setAddedFiles={setAddedFiles}
            usedFor="tenantChecks"
            step={step}
            getNextStep={getNextStep}
          />
        );
      case 4:
        return (
          <BasicInfoModal
            user={user}
            formData={guarantorFormData}
            setFormData={setGuarantorFormData}
            createUserProfile={(data) => createUserProfile(data, "llGuarantor")}
            formHandler={formHandler}
            requiresSubcategory={requiresSubcategory}
            originalUser={originalUser}
            phoneNumber={guarantorPhoneNumber}
            setPhoneNumber={setGuarantorPhoneNumber}
            selectedProtectedHousingDocs={selectedProtectedHousingDocs}
            setSelectedProtectedHousingDocs={setSelectedProtectedHousingDocs}
            usedFor="tenantChecks"
            llGuarantor={true}
          />
        );
      case 5:
        return (
          <TenantChecksUploadDocument
            user={user}
            formHandler={formHandler}
            updateUserProfile={updateUserProfile}
            additionalInfo={additionalInfo}
            step={step}
            getNextStep={getNextStep}
          />
        );

      case 6:
        return (
          <TenantChecksIncomeModel
            user={user}
            additionalInfo={additionalInfo}
            formHandler={formHandler}
            updateUserProfile={updateUserProfile}
            originalUser={originalUser}
            getNextStep={getNextStep}
            setShowPassedModal={setShowPassedModal}
          />
        );

      default:
        return (
          <BasicInfoModal
            user={user}
            formData={formData}
            setFormData={setFormData}
            createUserProfile={createUserProfile}
            formHandler={formHandler}
            requiresSubcategory={requiresSubcategory}
            originalUser={originalUser}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            selectedProtectedHousingDocs={selectedProtectedHousingDocs}
            setSelectedProtectedHousingDocs={setSelectedProtectedHousingDocs}
            usedFor="tenantChecks"
          />
        );
    }
  };

  return (
    <>
      {pageLoading ? (
        <div id="main-app-loader">
          <Loader />
        </div>
      ) : (
        renderStep()
      )}

      {showPassedModal && (
        <ProcessPassedModal
          showPopup={showPassedModal}
          heading={_t(
            "tenantProfileGenerated",
            "on ll side when landlords completes a tenant check"
          )}
          subheading={_t(
            "theTenantProfileHasBeenSuccessfullyGenerated",
            "on ll side when landlords completes a tenant check"
          )}
          buttonText={_t("doneAndViewProfile")}
          handleClick={(e) => {
            navigate(`/${workspaceId}/created-tenant`);
          }}
        />
      )}
    </>
  );
}

export default TenantChecksOnboarding;
